import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import CryptoJS from "crypto-js";

// import { AppConstant } from "../../config/AppConstants.js";
import { handleApiResponse } from "../../hooks/notificationHelper.js";
import useRobotsMeta from "../../hooks/useRobotsMeta.js";
import { useUser } from "../../context/UserContext.jsx";
import { LoginWithPassword } from "../apiUtils.jsx";
//import LoginUI from "./LoginUI.jsx";
import LoginUI from "./LoginUI.jsx";
import { getAccessToken, getLoginId, setAccessToken, setGuId, setRefreshToken, getRefreshToken, getGuId, setMakeInvitations, setSSOLogin, persistSession, hydrateProfileFromRecords, isValidStoredValue, getImpersonated } from "../../utils/SecurityService.js";
import { EMAIL_PATTERN, isPasswordLongEnough, PASSWORD_MIN_LENGTH } from "../../utils/CommonUtils";
import { CheckUserVehicle_api, FetchCompaniesByEmail_api, ForgotPassword_api, ManualLogin_api, Profile_api, RedirectToUrl_api, Register_api } from "./LoginAPI.js";
import { AppConstant } from "../../config/AppConstants.js";
import { getDesktopParams } from "../../utils/desktopParams.js";
import { fetchDeskSyncTokenPair } from "../../api/tokenRefresh.js";
import { useTranslation } from 'react-i18next';
import { app } from "@microsoft/teams-js";
import GTM from "react-gtm-module";
import { DELETE_ACCOUNT_STORAGE_KEY } from "../DeleteAccount/DeleteAccount.jsx";
import { logoutUser } from "../Sidebar/SidebarAPI.jsx";

// DeskSync (app=desktop) handoff: how long to leave the browser on the launch URL
// before resetting it to a clean login page. Custom schemes fire no load/error event,
// so there is nothing to await — this delay is what lets the OS handler (and the
// browser's "open external app?" confirmation dialog) consume the pending
// ronspot-agent:// navigation before we navigate the tab ourselves.
const DESKSYNC_RESET_DELAY_MS = 5000;

// Once the deep link has been handed off, the browser was only a launcher: return the
// tab to a bare login page. Email/password come back empty for free — both are fresh
// useState("") after a full document load.
//
// `clearSession` is only needed by the already-logged-in boot handoff, where a stored
// session would make "/" forward straight to /calendar instead of showing the login
// form. The manual-login handoff persists nothing, so it just reloads.
//
// When it does clear, it does so WITHOUT calling the logout API on purpose:
// UserLogout_api sends the website's GuId + RefreshToken, which may revoke the pair
// just handed to DeskSync (per-user vs per-token revocation is a backend concern — see
// api/tokenRefresh.js). Clearing locally logs the browser out and leaves DeskSync's
// tokens valid.
//
// Uses location.replace, not href: a plain href assignment leaves ?app=desktop in
// history, so Back would return to the launch URL and re-fire the whole handoff.
// Returns a disarm function so a caller with a cleanup hook can cancel a pending reset.
const scheduleDeskSyncLoginReset = ({ clearSession = false } = {}) => {
    const timer = setTimeout(() => {
        if (clearSession) {
            localStorage.clear();
            sessionStorage.clear();
        }
        window.location.replace("/");
    }, DESKSYNC_RESET_DELAY_MS);

    return () => clearTimeout(timer);
};

function LoginEvent() {
    const { t, i18n } = useTranslation("common");
    const [isLoading, setIsLoading] = useState(false);
    const [ClientId, setClientId] = useState(AppConstant.DEFAULT_CLIENT_ID);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [error, setError] = useState(null);
    const [fieldError, setFieldError] = useState({});
    const history = useHistory();

    const { setUser } = useUser();
    // Validation patterns rules
    const emailPattern = EMAIL_PATTERN;
    const nameLetterRule = useMemo(() => {
        const raw = t("react_name_regex");
        // i18next returns the key itself when missing — guard against that.
        if (raw && raw !== "react_name_regex") {
            try {
                return new RegExp(raw);
            } catch {
                // malformed pattern in a locale file → fall through to default
            }
        }
        return /^[A-Za-zÀ-ÿ ]+$/;
    }, [t, i18n.language]);
    const passwordletters = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*\W)/;

    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [loadingCompanies] = useState(false);
    const [loginMethods, setLoginMethods] = useState([]);
    const [loadingLoginMethods, setLoadingLoginMethods] = useState(false);
    const [selectedLoginMethod, setSelectedLoginMethod] = useState(null);

    const PasswordT = CryptoJS.MD5(password).toString(CryptoJS.enc.Hex);
    const PasswordN = CryptoJS.SHA512(password).toString(CryptoJS.Hex);

    const [showManualLogin, setShowManualLogin] = useState(false);
    const [showForgotPasswordMessage, setShowForgotPasswordMessage] = useState(false);
    const [forgotPasswordMessage, setForgotPasswordMessage] = useState("");
    const [isTermsAccepted, setIsTermsAccepted] = useState(false);
    const [showTermsAcceptance, setShowTermsAcceptance] = useState(false); // RON-5272: hidden until API enables it
    const [isPasswordStageUnlocked, setIsPasswordStageUnlocked] = useState(false); // RON-5272 P5: gate password behind terms Confirm (single manual method)

    const [isNewUser, setIsNewUser] = useState(null);

    const [notification, setNotification] = useState(null);

    const setEmailApiError = (message) => {
        const errorMessage = message || t("email_not_valid");
        setFieldError({ field: "", message: "" });
        setError(errorMessage);
    };

    const isEmailRelatedError = (message = "") => {
        const normalizedMessage = String(message).toLowerCase();
        return normalizedMessage.includes("email");
    };

    const getLoginDiscoveryPayload = (response) => {
        const data = response?.Data || response?.data || {};
        const records = data?.Records || data?.records || {};
        const rawCompanies = records?.Companies || records?.companies || [];
        const rawLoginMethods = records?.LoginMethods || records?.loginMethods || [];

        const companies = rawCompanies.map((company) => ({
            ...company,
            id: company?.id ?? company?.Id ?? company?.CompanyId ?? company?.CompanyID ?? "",
            CompanyName: company?.CompanyName || company?.companyName || company?.Name || company?.name || "",
        }));

        const loginMethods = rawLoginMethods.map((method) => ({
            ...method,
            ssotype: String(
                method?.ssotype ??
                method?.SSOType ??
                method?.SsoType ??
                method?.SelectedMethod ??
                method?.selectedMethod ??
                method?.Type ??
                method?.type ??
                ""
            ).toLowerCase(),
            Name: method?.Name || method?.name || method?.DisplayName || method?.displayName || "",
            companyId: String(
                method?.companyId ??
                method?.CompanyId ??
                method?.companyID ??
                method?.CompanyID ??
                ""
            ),
        }));

        return {
            responseCode: data?.ResponseCode ?? data?.responseCode,
            companies,
            loginMethods,
            isNewUser: records?.IsNewUser ?? records?.isNewUser,
            showTermsAcceptance: Number(records?.showTermsAcceptance ?? records?.ShowTermsAcceptance) === 1,
        };
    };

    useEffect(() => {
        GTM.initialize({ gtmId: "GTM-WBZXCD8" });
        GTM.dataLayer({
            dataLayer: {
                event: "page_view",
                page_path: window.location.pathname,
                page_title: "Login",
            },
        });
    }, []);


    useEffect(() => {
        document.title = `${t("react_web_login")} | Ronspot`;
      }, []);

    // Staging/preprod only: REACT_APP_LOGIN_ROBOTS is unset in production.
    useRobotsMeta(process.env.REACT_APP_LOGIN_ROBOTS);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (window.$?.fn?.validate) {
            window.$(".login-form").validate();
        }
    }, []);

    useEffect(() => {
        setShowManualLogin(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompany]);

    const ToCheckVehicle = async (uid) => {
        try {
            const requestParams = {
                GuId: uid || getGuId(),
            };
            const response = await CheckUserVehicle_api(requestParams);
            
            console.log("Vehicle check params:", requestParams);
            console.log("Vehicle check response:", response);

            if (response?.Data?.ResponseCode !== 200) {
                throw new Error("Vehicle verification failed");
            }

            const showVehiclePopup = Number(response?.Data?.Records?.ShowVehiclePopup ?? 0);
            const parkingZonesAssigned = Number(response?.Data?.Records?.ParkingZonesAssigned ?? 0);

            if (parkingZonesAssigned === 1 && showVehiclePopup === 1) {
                localStorage.setItem("vehiclenotavailable", "true");
            } else {
                localStorage.removeItem("vehiclenotavailable");
            }
        } catch (error) {
            console.error("Vehicle check error:", error);
            throw error;
        }
    };

    const redirectToHome = async () => {
        try {
            await ToCheckVehicle();
        } catch (error) {
            console.error("ToCheckVehicle API failed:", error);
        } finally {
            const mcpRedirect = sessionStorage.getItem("mcp_oauth_redirect");
            const shouldOpenDeleteAccount = localStorage.getItem(DELETE_ACCOUNT_STORAGE_KEY) === "1";
            if (mcpRedirect) {
                sessionStorage.removeItem("mcp_oauth_redirect");
                window.location.href = mcpRedirect;
            } else if (shouldOpenDeleteAccount) {
                history.replace("/profile");
            } else {
                history.replace("/calendar");
            }
        }
    };

    const hydrateSessionFromProfile = (records = {}) => {
        hydrateProfileFromRecords(records);
        if (records?.MakeInvitations !== undefined && records?.MakeInvitations !== null) setMakeInvitations(records.MakeInvitations);

        setUser({
            firstName: records?.FirstName || "",
            lastName: records?.LastName || "",
        });
    };

    const handleSingleLoginMethod = async (methods = [], showTerms = false, termsAccepted = false) => {
        if (!Array.isArray(methods) || methods.length !== 1) {
            return false;
        }

        const singleMethod = methods[0];
        setSelectedLoginMethod(singleMethod);

        if (singleMethod?.ssotype === "manual") {
            setShowManualLogin(true);
            return true;
        }

        // RON-5272: do NOT auto-forward a single SSO method while terms are
        // required but not yet accepted — the user must tick the box first.
        if (showTerms && !termsAccepted) {
            return false;
        }

        await handleRedirect(singleMethod);
        return true;
    };

    const shouldHideSingleSsoMethodList = (methods = []) => (
        Array.isArray(methods) &&
        methods.length === 1 &&
        methods[0]?.ssotype !== "manual"
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setFieldError({ field: "", message: "" });
        //sessionStorage.clear();
        //localStorage.clear();
        let data;

        if (!email || email.trim() === "") {
            setFieldError({
                field: "email",
                message: t("enter_email_add")
            });
            setError(t("enter_email_add"));
            return;
        }
        
        if (showTermsAcceptance && !isTermsAccepted) {
            setError(t("please_accept_terms_conditions_proceed"));
            return;
        }
        else { setError(null); }

        if (!emailPattern.test(email)) {
            setFieldError({
                field: "email",
                message: t("email_not_valid")
            });
            setError(t("email_not_valid"));
            return;
        }

        try {
            // Register New User
            if (isNewUser === 1 && showManualLogin) {

                if (showTermsAcceptance && !isTermsAccepted) {
                    setError(t("please_accept_terms_conditions_proceed"));
                    return;
                }

                if (!email || email.trim() === "") {
                    setError(t("enter_email_add"));
                    return;
                }

                if (!emailPattern.test(email)) {
                    setError(t("email_not_valid"));
                    return;
                }

                if (!firstName || firstName.trim() === "") {
                    setFieldError({
                        field: "firstName",
                        message: t("pleaseenter_firstname")
                    });
                    return;
                }
                else if (!nameLetterRule.test(firstName)) {
                    setFieldError({
                        field: "firstName",
                        message: t("not_allow_char")
                    });
                    return;
                }

                if (!lastName || lastName.trim() === "") {
                    setFieldError({
                        field: "lastName",
                        message: t("pleaseenter_lastname")
                    });
                    return;
                }
                else if (!nameLetterRule.test(lastName)) {
                    setFieldError({
                        field: "lastName",
                        message: t("not_allow_char")
                    });
                    return;
                }

                if (!password || password.trim() === "") {
                    setFieldError({
                        field: "password",
                        message: t("react_please_enter_password")
                    });
                    return;
                }
                // Registration creates the password, so it uses the same rules as Reset
                // Password and Settings: shared minimum length, then passwordletters.
                else if (!isPasswordLongEnough(password)) {
                    setFieldError({
                        field: "password",
                        message: t("Mini_length_s_password").replace("%s", PASSWORD_MIN_LENGTH)
                    });
                    return;
                }
                else if (!passwordletters.test(password)) {
                    setFieldError({
                        field: "password",
                        message:
                            t("react_password_requirements")
                    });
                    return;
                }

                // ✅ clear error if all valid
                setFieldError({ field: "", message: "" });

                // The register call needs the company the user is signing up to. The
                // validation response may carry it on the LoginMethods entries instead of a
                // Companies list, so prefer an explicit company selection, then the CompanyID
                // of the method the user actually picked (any number of methods), then the
                // single CompanyID shared by all returned methods. Differing company IDs with
                // no method picked stay empty rather than guessing a company.
                const methodCompanyIds = Array.isArray(loginMethods)
                    ? [...new Set(loginMethods.map((method) => method?.companyId).filter(Boolean))]
                    : [];
                const sharedMethodCompanyId =
                    methodCompanyIds.length === 1 ? methodCompanyIds[0] : "";

                const requestParams = {
                    firstName,
                    lastName,
                    emailId: email,
                    password,
                    companyId: selectedCompany?.id
                        || selectedLoginMethod?.companyId
                        || sharedMethodCompanyId
                        || "",
                    ClientId,
                    RequestFrom: AppConstant.RequestFrom,
                    UniqueId: AppConstant.RegisterUserUniqueId
                };

                const response = await Register_api(requestParams);

                if (response?.Data?.ResponseCode === 200) {
                    history.push("/RegisterSuccess");
                } else {
                    const errorMessage = response?.Data?.ErrorMessage;
                    if (isEmailRelatedError(errorMessage)) {
                        setEmailApiError(errorMessage);
                    } else {
                        setError(errorMessage);
                    }
                }
            }

            // Manual Login for Existing User without SSO setup
            else if (showManualLogin) {
                // RON-5272 P5: single manual method + terms → the first Confirm click reveals the
                // password field; the login itself happens on the next Confirm. Terms acceptance is
                // already enforced by the gate at the top of handleSubmit, so ticking is required first.
                const isSingleManualMethod =
                    Array.isArray(loginMethods) &&
                    loginMethods.length === 1 &&
                    loginMethods[0]?.ssotype === "manual";
                if (showTermsAcceptance && isSingleManualMethod && !isPasswordStageUnlocked) {
                    setIsPasswordStageUnlocked(true);
                    return;
                }

                if (!password || password.trim() === "") {
                    setFieldError({
                        field: "password",
                        message: t("react_please_enter_password")
                    });
                    return;
                }
                // Login only checks that a password was entered. The complexity rule
                // (passwordletters) applies when a password is created (registration above),
                // not when signing in; the API rejects a wrong password itself.

                // ✅ clear error if all valid
                setFieldError({ field: "", message: "" });

                const requestParams = {
                    ClientId,
                    PrimaryEmail: email,
                    Password: password
                };
                const data = await ManualLogin_api(requestParams);
                console.log("ManualLogin_api response:", data);
                if (!data) {
                    const errorMessage = data?.Data?.ErrorMessage;
                    if (isEmailRelatedError(errorMessage)) {
                        setEmailApiError(errorMessage);
                    } else {
                        setError(errorMessage);
                    }
                    return;
                }
                const { Records, ResponseCode, ErrorMessage } = data.Data;
                if (ResponseCode !== 200) {
                    if (isEmailRelatedError(ErrorMessage)) {
                        setEmailApiError(ErrorMessage);
                    } else {
                        setError(ErrorMessage || "Login failed");
                    }
                    return;
                }
                // Desktop-launch flag, read directly from the current URL: the handoff
                // must run only when the app was actually launched with app=desktop
                // (i.e. RequestFrom:"desktop" was sent on this /User/login call), not
                // merely because a `Url` is present.
                const isDesktopLaunch = getDesktopParams()?.RequestFrom === "desktop";
                // Desktop-launch manual login (app=desktop): the backend returns the
                // normal login Records object PLUS a `Url` (the
                // login/common/response?atoken=…&rtoken=…&uid=… redirect). Navigate
                // top-level so the browser follows the 302 to the ronspot-agent://
                // deep link and hands the session to the DeskSync app. A normal web
                // login is not a desktop launch and skips this branch entirely.
                // (An array shape, Records:[{Url}], is also tolerated.)
                if (isDesktopLaunch ) {
                    const desktopRedirectUrl = Array.isArray(Records) ? Records[0]?.Url : Records?.Url;
                    if (desktopRedirectUrl) {
                        window.location.href = desktopRedirectUrl;
                        // This URL 302s to ronspot-agent://, so Chrome hands off to the OS
                        // and ABANDONS the navigation — the login page is left in place with
                        // the typed email/password and ?app=desktop still on it. Reload to a
                        // bare "/" once DeskSync has had time to open, which clears both
                        // fields (fresh useState("")) and drops the querystring. Nothing is
                        // persisted on this path, so there is no session to clear. If the
                        // navigation ever does complete, the document unloads and the timer
                        // dies with it.
                        scheduleDeskSyncLoginReset();
                        return;
                    }
                }
                if (!isDesktopLaunch) {
                // User Context

                setUser({
                    firstName: Records.FirstName,
                    lastName: Records.LastName,
                });
                // set in sessionStorage
                localStorage.setItem("Notifications", Records.Notifications);
                persistSession({
                    isSSOLogin: 0,
                    accessToken: Records.AccessToken,
                    refreshToken: Records.RefreshToken,
                    guid: Records.guid,
                    loginId: Records.id,
                    firstName: Records.FirstName,
                    lastName: Records.LastName,
                    email: Records.PrimaryEmail,
                    makeInvitations: Records.MakeInvitations,
                    hasEmergencyRoles: Records.HasEmergencyRoles,
                });
                console.log("Login Successful — Tokens Saved");
                const guid = getGuId();

                if (!guid || guid.trim() === "") {
                    return;
                }
                await redirectToHome();
            }
            }
            // SSO Login Flow - Fetch Companies and Login Methods
            else {
                console.log("Debug Mode Login with Email:", email, "and Company:", selectedCompany);

                const reqParams = {
                    ClientId,
                    PrimaryEmail: email,
                    CompanyId: selectedCompany ? selectedCompany.id : "",
                };
                console.log("FetchCompaniesByEmail -reqParams-", reqParams);

                FetchCompaniesByEmail_api(reqParams)
                    .then(async (response) => {
                        const { responseCode, companies: fetchedCompanies, loginMethods: fetchedLoginMethods, isNewUser: fetchedIsNewUser, showTermsAcceptance: fetchedShowTerms } =
                            getLoginDiscoveryPayload(response);
                        console.log("FetchCompaniesByEmail response-->", response);
                        if (responseCode === 200) {
                            console.log("LoginMethods fetched:", fetchedLoginMethods);
                            setCompanies(fetchedCompanies);
                            setIsNewUser(fetchedIsNewUser);
                            setShowTermsAcceptance(fetchedShowTerms);

                            if (!shouldHideSingleSsoMethodList(fetchedLoginMethods)) {
                                setLoginMethods(fetchedLoginMethods);
                            }

                            if (await handleSingleLoginMethod(fetchedLoginMethods, fetchedShowTerms, isTermsAccepted)) {
                                return;
                            }

                            if (fetchedCompanies.length === 1 && fetchedLoginMethods.length === 0) {
                                setSelectedCompany(fetchedCompanies[0]);
                            }
                        }
                        else {
                            const errorMessage = response?.Data?.ErrorMessage;
                            if (isEmailRelatedError(errorMessage)) {
                                setEmailApiError(errorMessage);
                            } else {
                                setError(errorMessage);
                            }
                        }
                    })
                    .catch((err) => {
                        console.error(err);
                    });
            }

        } catch (err) {
            console.error("Login Error:", err);
            setError("Login failed.");
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {

        if (!selectedCompany) return;

        setLoadingLoginMethods(true);
        setLoginMethods([]);
        console.log("Selected company changed:", selectedCompany);
        const reqParams = {
            ClientId,
            PrimaryEmail: email,
            CompanyId: selectedCompany ? selectedCompany.id : "",
        };

        FetchCompaniesByEmail_api(reqParams)
            .then(async (response) => {
                console.log("Login methods response:", response);
                const { responseCode, loginMethods: fetchedLoginMethods, showTermsAcceptance: fetchedShowTerms } =
                    getLoginDiscoveryPayload(response);

                if (responseCode === 200) {
                    setShowTermsAcceptance(fetchedShowTerms);
                    if (!shouldHideSingleSsoMethodList(fetchedLoginMethods)) {
                        setLoginMethods(fetchedLoginMethods);
                    }
                    await handleSingleLoginMethod(fetchedLoginMethods, fetchedShowTerms, isTermsAccepted);
                }
            })
            .catch((err) => {
                console.error("Login methods error:", err);
            })
            .finally(() => setLoadingLoginMethods(false));

    }, [selectedCompany]);

    const handleEmailChange = (value) => {
        setEmail(value);
        setFieldError({ field: "", message: "" });
        setPassword("");

        // ✅ RESET EVERYTHING HERE (parent owns state)
        setSelectedCompany(null);
        setCompanies([]);
        setLoginMethods([]);
        setShowManualLogin(false);
        setShowForgotPasswordMessage(false);
        setForgotPasswordMessage("");
        setError(null);
        setSelectedLoginMethod(null)
        setIsPasswordStageUnlocked(false); // RON-5272 P5: restart the terms→password gate on email change
        // RON-5272: hide the terms checkbox and drop any prior acceptance on email change.
        // No validation call here — showTermsAcceptance is re-fetched by the discovery
        // branch of handleSubmit on the next Confirm click.
        setShowTermsAcceptance(false);
        setIsTermsAccepted(false);
    };
    const handleForgotPassword = async () => {
        setError(null);

        if (!email || email.trim() === "") {
            setError(t("enter_email_add"));
            return;
        }

        if (!emailPattern.test(email)) {
            setError(t("email_not_valid"));
            return;
        }

        try {
            const requestParams = {
                ClientId,
                PrimaryEmail: email,
                RequestFrom:AppConstant.RequestFrom
            };

            const response = await ForgotPassword_api(requestParams);
            const responseMessage =
                response?.Data?.ErrorMessage ||
                response?.Data?.ResponseMessage ||
                t("forgot_password_mail");

            setForgotPasswordMessage(responseMessage);
            setShowForgotPasswordMessage(true);

            setTimeout(() => {
                setShowForgotPasswordMessage(false);
                setForgotPasswordMessage("");
                setEmail("");
                setPassword("");
                setShowManualLogin(false);
                setSelectedCompany(null);
                setCompanies([]);
                setLoginMethods([]);
                setSelectedLoginMethod(null);
                setIsNewUser(null);
                setError(null);
            }, 5000);
        } catch (err) {
            console.error("Forgot password error:", err);
            setError(t("something_went_wrong"));
        }
    };
    const handleRedirect = async (loginMethodParam) => {
        try {
            if (showTermsAcceptance && !isTermsAccepted) {
                setError(t("please_accept_terms_conditions_proceed"));
                return;
            }
            else { setError(null); }
            const method = loginMethodParam || selectedLoginMethod;

            console.log("Selected login method:", method);

            if (!method) {
                setError("Please select a login method");
                return;
            }

            if (!email || email.trim() === "") { setError("Please enter an email address"); return; }
            if (!emailPattern.test(email)) {
                setError("Please provide a valid email address");
                return;
            }
            //console.log("selectedCompany-->", selectedCompany,selectedCompany.id)
            const redirectCompanyId =
                method?.companyId ||
                (selectedCompany ? selectedCompany?.id : "") ||
                "";
            console.log("Redirecting with Company ID:", redirectCompanyId);
            const requestParams = {
                CompanyId: redirectCompanyId,
                Isnewuser: isNewUser ?? 0,
                Email: email,
                RequestFrom: AppConstant.RequestFrom, //Never change this value as it's being used in API to identify the request is coming from React App or not and building URL accordingly for Staging/ LOCAL
                ClientId:ClientId,                   // ✅ required
                SelectedMethod: method?.ssotype || "microsoft"
            };

            console.log("Redirect API request:", requestParams);

            const response = await RedirectToUrl_api(requestParams);

            if (response?.Data?.ResponseCode === 200) {
                const redirectUrl = response?.Data?.Records?.[0]?.url;

                if (redirectUrl) {
                    window.location.href = redirectUrl;
                    setSSOLogin(1);
                } else {
                    setError("Redirect URL not found");
                }
            } else {
                setError(response?.Data?.ErrorMessage || "Redirect failed");
            }

        } catch (err) {
            console.error("Redirect error:", err);
            setError("Something went wrong");
        }
    };

    useEffect(() => {

        const existingToken = getAccessToken();
        const existingGuid = getGuId();
        const existingLoginId = getLoginId();

        const params = new URLSearchParams(window.location.search);
        const atoken = params.get("atoken");
        const rtoken = params.get("rtoken");
        const uid = params.get("uid");
        // Only treat the SSO callback as valid when every param is a real value — reject the
        // literal strings "undefined"/"null" so a malformed redirect never persists dead tokens.
        const IsQuerystringParaAvailable = [atoken, rtoken, uid].every((param) => isValidStoredValue(param?.trim()));

        const handleSsoCallback = async () => {
            if (!IsQuerystringParaAvailable) return;

            try {
                setIsLoading(true);
                setError(null);
                console.log("SSO Login Success");

                setSSOLogin(1);
                setAccessToken(atoken);
                setRefreshToken(rtoken);
                setGuId(uid);

                await fetchProfileData(uid);

                // Clean URL only after we've persisted the callback session
                window.history.replaceState({}, document.title, window.location.pathname);
            } catch (error) {
                console.error("SSO callback handling failed:", error);
                setError("Unable to complete SSO login.");
                setIsLoading(false);
            }
        };

        if (IsQuerystringParaAvailable) {
            handleSsoCallback();
            return;
        }

        // Only auto-forward to the app when the stored session is genuinely valid. Plain
        // truthiness is not enough: a corrupted session can hold the string "undefined" for the
        // access token (a truthy string), which would forward the user to a calendar whose APIs
        // all fail. Validating here keeps them on the login screen instead.
        if (
            isValidStoredValue(existingToken) &&
            isValidStoredValue(existingGuid) &&
            isValidStoredValue(existingLoginId)
        ) {
            // DeskSync handoff for an ALREADY-logged-in user: when the page is opened
            // with app=desktop, don't just load the website / go to /calendar — build
            // the ronspot-agent:// deep link straight from the existing session tokens
            // and hand off to the DeskSync app. Read app=desktop directly from the URL
            // (no localStorage needed here). `ronspot-agent://` is a fixed protocol
            // scheme, so nothing environment-specific is hardcoded.
            if (params.get("app") === "desktop") {
                // An impersonated session must never be handed off to DeskSync: the deep
                // link would hand the desktop app tokens minted for the impersonated user.
                // Tear the session down and drop back to login instead. Returns straight
                // away so the handoff below and `redirectToHome()` are both skipped.
                if (getImpersonated() == 1) {
                    // Keep the desktop-launch params on the login URL. getDesktopParams()
                    // reads them straight from window.location at request time (nothing is
                    // persisted, and the logout wipes storage anyway), so they must survive
                    // this redirect for the next login to still count as a desktop launch
                    // and resume the DeskSync handoff.
                    const desktopQuery = new URLSearchParams();
                    ["app", "clientId", "deviceId"].forEach((key) => {
                        const value = params.get(key);
                        if (value) desktopQuery.set(key, value);
                    });
                    const loginUrl = desktopQuery.toString() ? `/?${desktopQuery.toString()}` : "/";

                    (async () => {
                        await logoutUser(history, loginUrl); // Clear Local and Session Storage and redirect to login page
                    })();
                    return;
                }

                // Hand off to DeskSync with a NEW token pair minted just for it,
                // WITHOUT persisting it, so DeskSync gets a pair distinct from the
                // website's and the backend can tell the two apart. Runs in an async
                // IIFE because the effect callback itself is synchronous.
                //
                // The website's own tokens stay in storage only until the handoff has
                // been dispatched: scheduleDeskSyncLoginReset() then clears them and
                // returns the tab to a clean "/" login page, so a desktop launch never
                // leaves a logged-in browser session behind.
                let disarmLoginReset = null;
                (async () => {
                    try {
                        const { accessToken, refreshToken } = await fetchDeskSyncTokenPair();
                        const customUrl =
                            "ronspot-agent://auth?atoken=" + accessToken +
                            "&rtoken=" + refreshToken +
                            "&uid=" + existingGuid;
                            console.log("DeskSync token handoff-try URL:", customUrl);
                        window.location.href = customUrl;
                        disarmLoginReset = scheduleDeskSyncLoginReset({ clearSession: true });
                    } catch (error) {
                        console.error("DeskSync token handoff failed, using existing session:", error);
                        // Fallback so the desktop launch still works if minting fails:
                        // hand off with the existing session tokens.
                        const existingRefresh = getRefreshToken();
                        const customUrl =
                            "ronspot-agent://auth?atoken=" + existingToken +
                            "&rtoken=" + existingRefresh +
                            "&uid=" + existingGuid;
                        console.log("DeskSync token handoff-fallback URL:", customUrl);
                        window.location.href = customUrl;
                        disarmLoginReset = scheduleDeskSyncLoginReset({ clearSession: true });
                    }
                })();
                // Cancel a pending reset if the component goes away first, so the timer
                // can never clear storage or navigate after unmount.
                return () => {
                    if (disarmLoginReset) disarmLoginReset();
                };
            }
            redirectToHome();
        }
        /* else {
            window.location.href = "/"; // else goto-login new
        } */
    }, [history]);

    const fetchProfileData = async (uid) => {
        try {
            const requestParams = {
                GuId: uid, // or UserId depending on your API contract
            };
            const response = await Profile_api(requestParams);
            console.log("Fetching profile data with params:", requestParams);

            console.log("Profile API Response:", response);

            if (response?.Data?.ResponseCode === 200) {
                const Records = response?.Data?.Records;
                hydrateSessionFromProfile(Records);
                const guid = getGuId();

                if (!guid || guid.trim() === "") {
                    return;
                }
                setIsLoading(false);
                await redirectToHome();

            } else {
                console.error("Profile API failed:", response?.Data?.ErrorMessage);
                setError(response?.Data?.ErrorMessage || "Unable to load profile data.");
                setIsLoading(false);
            }

        } catch (error) {
            console.error("Profile API error:", error);
            setError("Unable to load profile data.");
            setIsLoading(false);
            throw error;
        }
    };

    useEffect(() => {
        if (!email || email.trim() === "") {
            setCompanies([]);
            setLoginMethods([]);
            setSelectedCompany(null);
            setSelectedLoginMethod(null);
            setShowManualLogin(false);
            setIsNewUser(null);
            setShowTermsAcceptance(false);
            setIsPasswordStageUnlocked(false); // RON-5272 P5: reset the terms→password gate when email is cleared
            setShowForgotPasswordMessage(false);
            setForgotPasswordMessage("");
            setError(null);
        }
    }, [email]);

    // RON-5272: clear ONLY the terms validation message once the user ticks the checkbox
    useEffect(() => {
        if (isTermsAccepted) {
            setError((prev) =>
                prev === t("please_accept_terms_conditions_proceed") ? null : prev
            );
        }
    }, [isTermsAccepted, t]);

    return (
        <LoginUI
            email={email}
            setEmail={handleEmailChange}
            setPassword={setPassword}
            setFirstName={setFirstName}
            setLastName={setLastName}
            handleSubmit={handleSubmit}
            error={error}
            fieldError={fieldError}
            setFieldError={setFieldError}

            companies={companies}
            selectedCompany={selectedCompany}
            setSelectedCompany={setSelectedCompany}
            loadingCompanies={loadingCompanies}
            loginMethods={loginMethods}
            loadingLoginMethods={loadingLoginMethods}

            showManualLogin={showManualLogin}
            setShowManualLogin={setShowManualLogin}
            isTermsAccepted={isTermsAccepted}
            setIsTermsAccepted={setIsTermsAccepted}
            showTermsAcceptance={showTermsAcceptance}
            isPasswordStageUnlocked={isPasswordStageUnlocked}
            handleForgotPassword={handleForgotPassword}
            showForgotPasswordMessage={showForgotPasswordMessage}
            forgotPasswordMessage={forgotPasswordMessage}
            handleRedirect={handleRedirect}
            selectedLoginMethod={selectedLoginMethod}
            setSelectedLoginMethod={setSelectedLoginMethod}
            isNewUser={isNewUser}
            isLoading={isLoading}
        />
    );
}

export default LoginEvent;

